import React from 'react';
import cx from 'classnames';
import { noop } from 'lodash';

import { applyCypressData, isFunction } from 'Utilities/helpers';
import useExpandable from 'Utilities/hooks/useExpandable';

import SectionButton from '../SectionButton/SectionButton';

import ExpandableIcon from './ExpandableIcon/ExpandableIcon';

import styles from './Expandable.module.scss';

export type ExpandableCaptionProps = {
	expanded: boolean;
	icon?: React.ReactNode;
};

export type ExpandableCaptionFunction = (props: ExpandableCaptionProps) => React.ReactNode;

type ExpandableProps = React.PropsWithChildren<{
	caption: ExpandableCaptionFunction;
	className?: string;
	expandedClassName?: string;
	icon?: React.FC<React.SVGProps<SVGSVGElement>> | null;
	id?: string;
	isExpanded?: boolean;
	onClick?: (expanded: boolean) => void;
	sectionButtonClassName?: string;
	wrapperProps?: Record<string, unknown>;
}>;

const Expandable: React.FC<ExpandableProps> = ({
	caption,
	children,
	className = '',
	expandedClassName = '',
	icon = null,
	id = null,
	isExpanded = false,
	onClick = noop,
	sectionButtonClassName = '',
	wrapperProps = null,
}) => {
	const { buttonProps, contentRef, expanded, maxHeight } = useExpandable({ id, isExpanded, onClick });

	const hasCaption = isFunction(caption);

	const classNames = cx(styles.component, className, {
		[styles.expanded]: expanded,
		[expandedClassName]: expanded,
	});

	return (
		<div className={classNames}>
			{hasCaption ? (
				<SectionButton className={sectionButtonClassName} {...buttonProps} {...applyCypressData(`${id}Button`)}>
					{caption({
						expanded,
						icon: <ExpandableIcon expanded={expanded} icon={icon} />,
					})}
				</SectionButton>
			) : null}
			<div
				{...wrapperProps}
				{...{
					'aria-expanded': expanded,
					'aria-hidden': !expanded,
					...(hasCaption && id ? { id } : null),
				}}
				className={cx(styles.contentWrapper)}
				style={{ maxHeight }}
			>
				<div ref={contentRef} className={cx('expandableContent', styles.content)}>
					{children}
				</div>
			</div>
		</div>
	);
};

export default Expandable;
